/**
 * PasskeyEnrollment — the ONE screen a sign-in held for a passkey enrols through, in an app that sets one up (SECREQ
 * S6b; PERMISSIONS-CANON-001).
 *
 * The company MFA policy can require a passkey for a role. A person who has none is held at sign-in (403
 * `PASSKEY_SETUP_REQUIRED`, a ten-minute enrolment token); `@puffin-core/auth-context` raises that hold as
 * `auth.factorEnrollment` and serves the begin / complete / finish actions over the token, and
 * `@puffin-core/login-shell`'s `<LoginForm>` renders this screen from them. An app that sets no passkey up hands the
 * person to one that does instead (login-shell's `<PasskeySetupHandoff>`, S6d).
 *
 * Steps (derived from the enrolment state, never duplicated):
 *   1. start — why, naming the app; "Create passkey" runs the ONE ceremony (`enrollPasskey`) over the hold's
 *      token-bound calls;
 *   2. backup — when the passkey turned two-step verification on, its backup codes, shown once; "Complete Setup"
 *      publishes the session (`onFinish`). With no codes to show, the screen finishes at once.
 * And one more state: `enrollment.alreadyEnrolled` — the account already has a passkey (a retry whose earlier success
 * response was lost). The screen says so and moves focus to "Sign in" (the cancel action).
 *
 * Every refusal is worded from the catalog by `registerErrorKey` — the ONE wording the in-session passkey card uses —
 * plus the held step's own case, a stale enrolment proof (the passkey seat's fresh-proof window is shorter than the
 * token's life). View-layer only: the actions are the context's, injected as props.
 *
 * @module @puffin-core/mfa-ui/PasskeyEnrollment
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { ENROLLMENT_PROOF_STALE } from '@puffin-core/factor-enrollment-codes';

import { enrollPasskey } from './enrollPasskey.js';
import { WebAuthnService } from './WebAuthnService.js';
import { registerErrorKey } from './hooks/usePasskeyManagement.js';
import { useMfaTranslate } from './hooks/useMfaTranslate.js';
import { MfaBackupStep } from './internal/MfaBackupStep.jsx';

/**
 * The catalog key a held enrolment's refusal is worded by: a stale enrolment proof (the ONE vocabulary's
 * `ENROLLMENT_PROOF_STALE`, the passkey seat's refusal of a proof older than its fresh-proof window) reads as the
 * timed-out set-up, and every other refusal exactly as `registerErrorKey` words it.
 *
 * @param {*} err - what the enrolment rejected with
 * @returns {string} an `mfa` catalog key
 */
export function heldRegisterErrorKey(err) {
  if (err?.errorCode === ENROLLMENT_PROOF_STALE) return 'passkey_errorRegisterExpired';
  return registerErrorKey(err);
}

/** The account already has a passkey: say so, and offer the one action. Module-private. */
function PasskeyAlreadyEnrolledStep({ onSignIn, t }) {
  const str = useMfaTranslate(t);
  const signInRef = useRef(null);
  useEffect(() => {
    signInRef.current.focus();
  }, []);

  return (
    <div data-testid="passkey-enrollment-already-enrolled">
      {/* h2: the step renders inside the login shell's card, under its h1. */}
      <h2 className="mfa-challenge-title">{str('enroll_passkey_already_title')}</h2>
      <p className="mfa-challenge-subtitle" role="status">
        {str('enroll_passkey_already_body')}
      </p>
      <div className="mfa-setup-actions">
        <button ref={signInRef} type="button" className="btn btn-primary" onClick={onSignIn}>
          {str('enroll_already_btn_sign_in')}
        </button>
      </div>
    </div>
  );
}

PasskeyAlreadyEnrolledStep.propTypes = {
  onSignIn: PropTypes.func.isRequired,
  t: PropTypes.func,
};

/** No backup codes to show: the session is published at once. Module-private. */
function FinishOnMount({ onFinish }) {
  useEffect(() => {
    onFinish();
  }, [onFinish]);
  return null;
}

FinishOnMount.propTypes = { onFinish: PropTypes.func.isRequired };

/** The backup codes a first second factor earned, then Continue. Module-private. */
function PasskeyBackupStep({ backupCodes, onFinish, t }) {
  const [acknowledged, setAcknowledged] = useState(false);
  return (
    <MfaBackupStep
      backupCodes={backupCodes}
      acknowledged={acknowledged}
      onAcknowledgeChange={(event) => setAcknowledged(event.target.checked)}
      onComplete={onFinish}
      introKey="passkey_backupCodesIntro"
      t={t}
    />
  );
}

PasskeyBackupStep.propTypes = {
  backupCodes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFinish: PropTypes.func.isRequired,
  t: PropTypes.func,
};

/**
 * @param {Object} props
 * @param {string} props.appName - this app's brand, shown in the copy.
 * @param {{completed: boolean, backupCodes: ?string[], alreadyEnrolled: boolean}} props.enrollment -
 *   `auth.passkeyEnrollment`.
 * @param {boolean} props.loading - `auth.isLoading`.
 * @param {() => Promise<Object>} props.onBegin - `auth.beginPasskeyEnrollment`: the creation options for the hold's
 *   token; it REJECTS a refused step (the context records no sentence), and the screen words the refusal.
 * @param {(credential: Object, friendlyName: string) => Promise} props.onComplete - `auth.completePasskeyEnrollment`;
 *   same contract.
 * @param {() => void} props.onFinish - publishes the session the completion held (or raises the next hold).
 * @param {() => void} props.onCancel - `auth.cancelMfaEnrollment`.
 * @param {Object} [props.webAuthnService] - the browser's WebAuthn legs; a `WebAuthnService` when omitted.
 * @param {Function} [props.t] - an i18next translator (see `useMfaTranslate`).
 * @returns {React.JSX.Element}
 */
export function PasskeyEnrollment({
  appName,
  enrollment,
  loading,
  onBegin,
  onComplete,
  onFinish,
  onCancel,
  webAuthnService: injected,
  t,
}) {
  // Every refused step is worded HERE, from the catalog: the context records no sentence for it. Report, never also
  // throw: the click handler does not await, so a rethrow would be an unhandled rejection.
  const [refusalKey, setRefusalKey] = useState(null);
  const createRef = useRef(null);
  const webAuthnService = useMemo(() => injected || new WebAuthnService(), [injected]);
  const str = useMfaTranslate(t);

  const onStart = !enrollment.completed && !enrollment.alreadyEnrolled;
  useEffect(() => {
    if (onStart) createRef.current.focus();
  }, [onStart]);

  if (enrollment.alreadyEnrolled) {
    return <PasskeyAlreadyEnrolledStep onSignIn={onCancel} t={t} />;
  }

  if (enrollment.completed) {
    return enrollment.backupCodes ? (
      <PasskeyBackupStep backupCodes={enrollment.backupCodes} onFinish={onFinish} t={t} />
    ) : (
      <FinishOnMount onFinish={onFinish} />
    );
  }

  const create = () => {
    setRefusalKey(null);
    enrollPasskey({
      authService: { getPasskeyRegisterOptions: onBegin, registerPasskey: onComplete },
      webAuthnService,
    }).catch((err) => {
      setRefusalKey(heldRegisterErrorKey(err));
    });
  };

  return (
    <div data-testid="passkey-enrollment">
      {/* h2: the step renders inside the login shell's card, under its h1. */}
      <h2 className="mfa-challenge-title">{str('enroll_passkey_title')}</h2>
      <p className="mfa-challenge-subtitle">{str('enroll_passkey_intro', { appName })}</p>
      {refusalKey ? (
        <div className="form-error" role="alert">
          {str(refusalKey)}
        </div>
      ) : null}
      <div className="mfa-setup-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          {str('enroll_btn_cancel')}
        </button>
        <button
          ref={createRef}
          type="button"
          className="btn btn-primary"
          onClick={create}
          disabled={loading}
        >
          {str('enroll_passkey_btn_create')}
        </button>
      </div>
    </div>
  );
}

PasskeyEnrollment.propTypes = {
  appName: PropTypes.string.isRequired,
  enrollment: PropTypes.shape({
    completed: PropTypes.bool.isRequired,
    backupCodes: PropTypes.arrayOf(PropTypes.string),
    alreadyEnrolled: PropTypes.bool.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onBegin: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  webAuthnService: PropTypes.shape({
    prepareRegistrationOptions: PropTypes.func,
    createCredential: PropTypes.func,
    formatRegistrationResponse: PropTypes.func,
  }),
  t: PropTypes.func,
};

export default PasskeyEnrollment;
